import { useEffect, useState } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import {
  Type,
  Image,
  TextLink,
  breakpoint,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { usePagination } from '@hooks/usePagination';
import { useShowUnclearedVideos } from '@hooks/useShowUnclearedVideos';
import { DataTable } from '@components/shared/DataTable';
import { DateRange } from '@components/shared/DateRange/types';
import { AnchorWithHover } from '@components/shared/styles';
import { getArtistTranslationKey } from '@helpers/artistUtils';
import { RELEASE_DATE_SORT, SORT_DIRECTION } from '@helpers/constants';
import { fromISOToMomentObject } from '@helpers/dateTimeUtils';
import { getUriId } from '@helpers/utils';
import {
  formatUnclearedVideosData,
  getLoadingSkeletonData,
  UnclearedVideosFormatted,
} from './helper';
import { UnclearedVideosTableExpansionRow } from './UnclearedVideosTableExpansionRow';

export interface UnclearedVideoTableProps {
  licensors: string[];
  dateRange?: DateRange;
  setTotal: (total: number) => void;
}

const ArtistDisplay = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  a {
    padding-left: 12px;
  }
`;

const ArtistName = styled(Type)`
  @media screen and (max-width: ${breakpoint.screenSmMin}) {
    display: none;
  }
`;

export const UnclearedVideosTable: React.VFC<UnclearedVideoTableProps> = ({
  licensors,
  dateRange,
  setTotal,
}: UnclearedVideoTableProps) => {
  const t = useT();
  const { limit, page, renderPagination } = usePagination();
  const [releaseDateSortDirection, setReleaseDateSortDirection] =
    useState<string>('none');
  const {
    data = { video: [], total: undefined },
    status,
    isLoading,
  } = useShowUnclearedVideos(
    page,
    limit,
    licensors,
    dateRange,
    releaseDateSortDirection,
  );

  const getUnclearedVideosData = (): UnclearedVideosFormatted[] => {
    if (isLoading) {
      return getLoadingSkeletonData(limit);
    }

    if (status !== 200) {
      return [];
    }

    return formatUnclearedVideosData(data.video);
  };
  const unclearedVideosData = getUnclearedVideosData();

  useEffect(() => {
    if (data?.total !== undefined) {
      setTotal(data.total);
    }
  }, [data?.total, setTotal]);
  return (
    <>
      <DataTable
        headers={[
          {
            width: '29%',
            property: 'trackName',
            label: t(
              'atlas.breadcrumbs.track',
              'Track',
              'Text for a breadcrumb link. Refers to "Track" in the noun form',
            ),
            renderRow: row => (
              <ArtistDisplay>
                <Image
                  alt={`${row.trackName} avatar`}
                  src={row.albumCoverArtImage}
                  crop
                  imageHeight="48px"
                  imageWidth="48px"
                />
                <Link
                  href={{
                    pathname: `/release/${getUriId(
                      row.albumUri,
                    )}/track/${getUriId(row.uri)}`,
                    query: {
                      fromRelease: row.albumUri,
                    },
                  }}
                  passHref
                  legacyBehavior
                >
                  <AnchorWithHover
                    onClick={evt => {
                      evt.stopPropagation();
                    }}
                  >
                    <Type variant="viola" semanticColor="textBase">
                      <div>
                        <Type
                          variant="viola"
                          weight="bold"
                          semanticColor="textBrightAccent"
                        >
                          {row.trackName}
                        </Type>
                      </div>
                      <ArtistName variant="viola" semanticColor="textSubdued">
                        {row.artists
                          .map(
                            artist =>
                              `${artist.name} (${getArtistTranslationKey(
                                artist.role,
                                t,
                              )})`,
                          )
                          .join(', ') || ''}
                      </ArtistName>
                    </Type>
                  </AnchorWithHover>
                </Link>
              </ArtistDisplay>
            ),
          },
          {
            width: '10%',
            property: 'isrc',
            label: t(
              'atlas.labels.isrc',
              'ISRC',
              'Label for ISRC. Identification tool for sound and music video recordings',
            ),
          },
          {
            width: '10%',
            property: 'upc',
            label: t(
              'atlas.labels.release_upc',
              'Release UPC',
              'Label for release UPC',
            ),
            renderRow: row => <>{row.upc ? row.upc : '-'}</>,
          },
          {
            width: '23%',
            property: 'uri',
            label: 'URI',
            renderRow: row => (
              <TextLink
                standalone
                href={row.url}
                target="_blank"
                semanticColor="textBrightAccent"
              >
                {row.uri}
              </TextLink>
            ),
          },
          {
            width: '10%',
            property: 'releaseDate',
            label: t(
              'atlas.label.release_date',
              'Release date',
              'Label for the release date of a track. Refers to "Release date" in the noun form',
            ),
            renderRow: row => (
              <>
                {fromISOToMomentObject(row.releaseDate)?.format('YYYY-MM-DD')}
              </>
            ),
            isSortable: true,
            onClickSort(newSortByDirection, currentSortByDirection) {
              if (currentSortByDirection === RELEASE_DATE_SORT.DESCENDING) {
                setReleaseDateSortDirection(RELEASE_DATE_SORT.NONE);
              } else {
                setReleaseDateSortDirection(newSortByDirection);
              }
            },
            sortedByDirection: releaseDateSortDirection as SORT_DIRECTION,
          },
        ]}
        data={unclearedVideosData}
        isLoading={isLoading}
        expandable
        onExpandRow={row => (
          <UnclearedVideosTableExpansionRow
            patches={row.patches}
            batchId={row.batchId}
            deliveryDate={row.deliveryDate}
          />
        )}
      />
      {status === 200 &&
        !!data?.video.length &&
        renderPagination({
          totalResultRows: unclearedVideosData.length || 0,
        })}
    </>
  );
};
